import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/Calendar";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
	Calendar as CalendarIcon,
	Clock,
	DollarSign,
	CheckCircle,
	Check,
	ArrowLeft,
	ArrowRight,
	AlertCircle,
	X,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { DoulaListing, Package } from "@/types/index";
import { PaymentFlow } from "@/components/payment/PaymentFlow";

interface BookingFlowProps {
	doula: DoulaListing;
	onComplete: () => void;
	onCancel: () => void;
}

interface TimeSlot {
	time: string;
	available: boolean;
}

interface DoulaAvailability {
	date: string;
	availableSlots: string[];
	timezone?: string;
}

export const BookingFlow: React.FC<BookingFlowProps> = ({
	doula,
	onComplete,
	onCancel,
}) => {
	const { user } = useAuth();

	const onClose = () => {
		onCancel();
	};
	const [currentStep, setCurrentStep] = useState(1);
	const [selectedDate, setSelectedDate] = useState<Date | null>(null);
	const [selectedTime, setSelectedTime] = useState<string>("");
	const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
	const [notes, setNotes] = useState("");
	const [loading, setLoading] = useState(false);
	const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
	const [error, setError] = useState<string>("");
	const [appointmentData, setAppointmentData] = useState<any>(null);
	const [doulaAvailability, setDoulaAvailability] = useState<
		DoulaAvailability[]
	>([]);
	const [loadingAvailability, setLoadingAvailability] = useState(false);
	const [calendarDays, setCalendarDays] = useState<Date[]>([]);
	const [loadingCalendar, setLoadingCalendar] = useState(true);
	const [selectedTimezone, setSelectedTimezone] = useState<string>("");

	// Initialize timezone selection
	useEffect(() => {
		// Auto-detect parent's timezone as default
		const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		setSelectedTimezone(detectedTimezone);
	}, []);

	// Load doula's availability when timezone is selected
	useEffect(() => {
		if (selectedTimezone) {
			loadDoulaAvailability();
		}
	}, [selectedTimezone]);

	const loadDoulaAvailability = async (retryCount = 0) => {
		if (!doula.user_id) {
			setError(
				"Unable to load doula availability. Please try again later or contact the doula directly.",
			);
			return;
		}

		setLoadingAvailability(true);
		try {
			// Calculate date range - extend by 30 days each retry
			const daysToAdd = 30 * (retryCount + 1);
			// Start from tomorrow (24 hours minimum) instead of today
			const tomorrow = new Date();
			tomorrow.setDate(tomorrow.getDate() + 1);
			tomorrow.setHours(0, 0, 0, 0); // Set to beginning of day
			const startDate = tomorrow.toISOString().split("T")[0];
			const endDate = new Date(
				tomorrow.getTime() + daysToAdd * 24 * 60 * 60 * 1000,
			)
				.toISOString()
				.split("T")[0];

			// Use selected timezone instead of auto-detected
			const parentTimezone = selectedTimezone;

			const response = await fetch(
				`${(import.meta.env as any).VITE_SUPABASE_URL}/functions/v1/get-doula-availability`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
					body: JSON.stringify({
						doulaId: doula.id,
						startDate: startDate,
						endDate: endDate,
						parentTimezone: parentTimezone,
					}),
				},
			);

			const data = await response.json();

			if (!response.ok) {
				// Handle specific error cases
				if (
					data.error === "Doula has not connected Google Calendar" ||
					data.error === "Doula needs to reconnect Google Calendar"
				) {
					setError(
						"This doula has not connected their Google Calendar yet. Please contact them directly to schedule an appointment.",
					);
				} else if (response.status === 401) {
					setError("Authentication error. Please try logging out and back in.");
				} else {
					setError("Unable to load availability. Please try again later.");
				}
			} else {
				// The edge function returns data.availableSlots, not data.availability
				if (
					data &&
					data.data &&
					data.data.availableSlots &&
					data.data.availableSlots.length > 0
				) {
					// Group available slots by date
					const availabilityByDate: { [key: string]: any[] } = {};

					data.data.availableSlots.forEach((slot: any) => {
						// Group by date in parent's timezone, not UTC
						const slotDate = new Date(slot.startTime);
						const dateKey = slotDate.toLocaleDateString("en-CA", {
							timeZone: parentTimezone,
						}); // 'en-CA' gives YYYY-MM-DD format
						if (!availabilityByDate[dateKey]) {
							availabilityByDate[dateKey] = [];
						}
						availabilityByDate[dateKey].push(slot);
					});

					// Convert to the format expected by the component
					const availabilityData = Object.keys(availabilityByDate).map(
						(dateKey) => ({
							date: dateKey,
							availableSlots: availabilityByDate[dateKey]
								.map((slot: any) => {
									// Convert from doula's timezone to parent's timezone for display
									const slotDate = new Date(slot.startTime);
									const doulaTimezone = slot.timezone || "America/New_York";

									// Convert the time to parent's timezone for display
									const timeInParentTZ = slotDate.toLocaleTimeString("en-US", {
										hour: "numeric",
										minute: "2-digit",
										hour12: true,
										timeZone: parentTimezone,
									});

									return {
										time: timeInParentTZ,
										timestamp: slotDate.getTime(),
									};
								})
								.sort((a, b) => a.timestamp - b.timestamp) // Sort by timestamp
								.map((slot) => slot.time), // Extract just the time strings
							timezone: parentTimezone, // Use parent's timezone
						}),
					);

					setDoulaAvailability(availabilityData);

					// Set available days from edge function
					if (data.data.availableDays) {
						// Filter out any dates that are in the past or within 24 hours
						const now = new Date();
						const twentyFourHoursFromNow = new Date(
							now.getTime() + 24 * 60 * 60 * 1000,
						);

						const availableDays = data.data.availableDays
							.map((dateStr: string) => {
								// Parse date in local timezone to avoid UTC conversion issues
								const [year, month, day] = dateStr.split("-").map(Number);
								return new Date(year, month - 1, day); // month is 0-indexed
							})
							.filter((date: Date) => {
								// Ensure date is at least 24 hours in the future
								const dateAtStartOfDay = new Date(date);
								dateAtStartOfDay.setHours(0, 0, 0, 0);
								const minDateAllowed = new Date(twentyFourHoursFromNow);
								minDateAllowed.setHours(0, 0, 0, 0);
								return dateAtStartOfDay >= minDateAllowed;
							});

						setCalendarDays(availableDays);
					}
				} else if (retryCount < 2) {
					// No availability found, try extending the date range

					setLoadingAvailability(false);
					setTimeout(() => loadDoulaAvailability(retryCount + 1), 1000);
					return;
				} else {
					setError(
						"No availability found in the next 90 days. Please contact the doula directly to schedule an appointment.",
					);
				}
			}
		} catch (error) {
			setError("Unable to load availability. Please try again later.");
		} finally {
			setLoadingAvailability(false);
			setLoadingCalendar(false);
		}
	};

	const generateDefaultTimeSlots = (): TimeSlot[] => {
		return [
			{ time: "9:00 AM", available: true },
			{ time: "9:30 AM", available: true },
			{ time: "10:00 AM", available: true },
			{ time: "10:30 AM", available: true },
			{ time: "11:00 AM", available: true },
			{ time: "11:30 AM", available: true },
			{ time: "1:00 PM", available: true },
			{ time: "1:30 PM", available: true },
			{ time: "2:00 PM", available: true },
			{ time: "2:30 PM", available: true },
			{ time: "3:00 PM", available: true },
			{ time: "3:30 PM", available: true },
			{ time: "4:00 PM", available: true },
			{ time: "4:30 PM", available: true },
		];
	};

	useEffect(() => {
		if (selectedDate) {
			loadAvailableSlotsForDate();
		}
	}, [selectedDate, doulaAvailability]);

	const loadAvailableSlotsForDate = () => {
		if (!selectedDate) return;

		const dateString = selectedDate.toISOString().split("T")[0];
		const availabilityForDate = doulaAvailability.find(
			(avail) => avail.date === dateString,
		);

		if (availabilityForDate && availabilityForDate.availableSlots.length > 0) {
			// Use real availability from doula's calendar
			const slots = availabilityForDate.availableSlots.map((time) => ({
				time: time,
				available: true,
			}));
			setAvailableSlots(slots);
		} else {
			// No availability data for this date, show message
			setAvailableSlots([]);
			setError("No availability found for this date. Please try another date.");
		}
	};

	const handleDateSelect = (date: Date) => {
		setSelectedDate(date);
		setSelectedTime("");
		setError("");
		// Automatically advance to next step
		nextStep();
	};

	const handleTimeSelect = (time: string) => {
		setSelectedTime(time);
		// Auto-advance to next step when time is selected
		setTimeout(() => {
			setCurrentStep(3);
		}, 300); // Small delay for better UX
	};

	const handleBooking = async () => {
		if (!selectedDate || !selectedTime || !user) return;

		setLoading(true);
		setError("");

		try {
			// Format date and time for the API - avoid timezone conversion issues
			const appointmentDate = selectedDate.toLocaleDateString("en-CA"); // YYYY-MM-DD format without timezone conversion
			const appointmentTime = selectedTime;
			const parentTimezone = selectedTimezone;

			// Call the create-appointment edge function
			const response = await fetch(
				`${(import.meta.env as any).VITE_SUPABASE_URL}/functions/v1/create-appointment`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
					body: JSON.stringify({
						doulaUserId: doula.user_id,
						doulaListingId: doula.id,
						appointmentDate: appointmentDate,
						appointmentTime: appointmentTime,
						parentTimezone: parentTimezone,
						durationMinutes: 30,
						notes: notes,
						packageId: selectedPackage?.id || null,
					}),
				},
			);

			const data = await response.json();

			if (!response.ok) {
				throw new Error(data.error || "Failed to create appointment");
			}

			if (data?.error) {
				throw new Error(data.error);
			}

			setAppointmentData(data);
			setCurrentStep(4); // Success step
		} catch (error: any) {
			setError(
				error.message || "Failed to create appointment. Please try again.",
			);
		} finally {
			setLoading(false);
		}
	};

	const nextStep = () => {
		if (currentStep < 4) {
			setCurrentStep(currentStep + 1);
		}
	};

	const prevStep = () => {
		if (currentStep > 1) {
			setCurrentStep(currentStep - 1);
		}
	};

	const renderStepContent = () => {
		switch (currentStep) {
			case 1:
				return (
					<div className="space-y-4">
						<h3 className="text-lg font-semibold mb-4">
							Select Your Timezone & Date
						</h3>

						{/* Timezone Selection */}
						<div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4 mb-6">
							<div className="flex items-center mb-3">
								<AlertCircle className="h-5 w-5 text-blue-600 mr-2" />
								<p className="text-sm text-blue-800 font-medium">
									Select your timezone to see accurate appointment times
								</p>
							</div>
							<select
								value={selectedTimezone}
								onChange={(e) => setSelectedTimezone(e.target.value)}
								className="w-full p-3 border border-blue-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
							>
								<option value="">Select your timezone...</option>
								<option value="America/New_York">Eastern Time (EST/EDT)</option>
								<option value="America/Chicago">Central Time (CST/CDT)</option>
								<option value="America/Denver">Mountain Time (MST/MDT)</option>
								<option value="America/Phoenix">Arizona Time (MST)</option>
								<option value="America/Los_Angeles">
									Pacific Time (PST/PDT)
								</option>
								<option value="America/Anchorage">
									Alaska Time (AKST/AKDT)
								</option>
								<option value="Pacific/Honolulu">Hawaii Time (HST)</option>
							</select>
						</div>

						<div className="bg-blue-50 p-4 rounded-lg mb-4">
							<div className="flex items-center">
								<AlertCircle className="h-5 w-5 text-blue-600 mr-2" />
								<p className="text-sm text-blue-800">
									Appointments must be booked at least 24 hours in advance
								</p>
							</div>
						</div>
						<div className="space-y-3">
							{!selectedTimezone ? (
								<div className="text-center py-8">
									<p className="text-gray-600">
										Please select your timezone first to see available dates.
									</p>
								</div>
							) : loadingCalendar ? (
								<div className="text-center py-8">
									<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-4"></div>
									<p className="text-gray-600">
										Loading available dates for{" "}
										{selectedTimezone.split("/")[1].replace("_", " ")}...
									</p>
								</div>
							) : calendarDays.length === 0 ? (
								<div className="text-center py-8">
									<p className="text-gray-600">
										No available dates found. Please try another time or contact
										the doula directly.
									</p>
								</div>
							) : (
								calendarDays.map((date, index) => {
									const isFirstThree = index < 3;
									return (
										<motion.button
											key={index}
											onClick={() => handleDateSelect(date)}
											className={`w-full p-4 rounded-lg border transition-all duration-200 ${selectedDate?.toDateString() === date.toDateString()
													? "bg-primary-600 text-white border-primary-600 shadow-lg"
													: isFirstThree
														? "bg-gradient-to-br from-primary-50 to-primary-100 border-primary-200 hover:border-primary-300 hover:shadow-md"
														: "bg-white border-gray-200 hover:border-primary-300 hover:shadow-md"
												}`}
											whileHover={{ scale: 1.02 }}
											whileTap={{ scale: 0.98 }}
										>
											<div className="flex items-center justify-between">
												<div className="text-left">
													<div
														className={`font-semibold text-lg ${selectedDate?.toDateString() ===
																date.toDateString()
																? "text-white"
																: isFirstThree
																	? "text-primary-800"
																	: "text-gray-900"
															}`}
													>
														{date.toLocaleDateString("en-US", {
															weekday: "long",
															month: "long",
															day: "numeric",
															year: "numeric",
														})}
													</div>
													<div
														className={`text-sm mt-1 ${selectedDate?.toDateString() ===
																date.toDateString()
																? "text-white opacity-90"
																: isFirstThree
																	? "text-primary-700"
																	: "text-gray-500"
															}`}
													>
														{isFirstThree ? "✨ Available" : "Available"}
													</div>
												</div>
												<div
													className={`relative ${selectedDate?.toDateString() === date.toDateString()
															? "text-white"
															: isFirstThree
																? "text-primary-600"
																: "text-gray-400"
														}`}
												>
													{/* 3D Date Box */}
													<div className="bg-white border-2 border-gray-200 rounded-lg shadow-lg transform rotate-3 hover:rotate-0 transition-transform duration-200">
														<div className="bg-gray-100 px-2 py-1 text-xs font-bold text-gray-600 border-b border-gray-200 rounded-t-sm">
															{date
																.toLocaleDateString("en-US", { month: "short" })
																.toUpperCase()}
														</div>
														<div className="px-3 py-2 text-2xl font-bold text-gray-800">
															{date.getDate()}
														</div>
													</div>
												</div>
											</div>
										</motion.button>
									);
								})
							)}
						</div>
					</div>
				);

			case 2:
				return (
					<div className="space-y-4">
						<h3 className="text-lg font-semibold mb-4">Select a Time</h3>

						{/* Selected Date Display */}
						{selectedDate && (
							<div className="bg-gradient-to-br from-primary-50 to-primary-100 border border-primary-200 rounded-lg p-4 mb-6">
								<div className="flex items-center justify-center">
									<div className="text-center">
										<p className="text-lg font-semibold text-primary-800">
											{selectedDate.toLocaleDateString("en-US", {
												weekday: "long",
												month: "long",
												day: "numeric",
												year: "numeric",
											})}
										</p>
										<p className="text-sm text-primary-600">
											Available times in{" "}
											{selectedTimezone.split("/")[1].replace("_", " ")}{" "}
											timezone
										</p>
									</div>
								</div>
							</div>
						)}

						{loadingAvailability ? (
							<div className="flex items-center justify-center py-8">
								<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
								<span className="ml-3 text-gray-600">
									Loading available times...
								</span>
							</div>
						) : (
							<div className="grid grid-cols-2 md:grid-cols-3 gap-3">
								{availableSlots.map((slot, index) => (
									<motion.button
										key={index}
										onClick={() =>
											slot.available && handleTimeSelect(slot.time)
										}
										disabled={!slot.available}
										className={`p-3 rounded-lg border transition-colors ${selectedTime === slot.time
												? "bg-primary-600 text-white border-primary-600"
												: slot.available
													? "border-gray-200 hover:border-primary-300"
													: "border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed"
											}`}
										whileHover={slot.available ? { scale: 1.05 } : {}}
										whileTap={slot.available ? { scale: 0.95 } : {}}
									>
										<Clock className="h-4 w-4 mx-auto mb-1" />
										{slot.time}
										{!slot.available && (
											<div className="text-xs mt-1">Unavailable</div>
										)}
									</motion.button>
								))}
							</div>
						)}

						{availableSlots.length === 0 && !loadingAvailability && (
							<div className="text-center py-8">
								<Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
								<p className="text-gray-600">
									No available times for this date
								</p>
							</div>
						)}
					</div>
				);

			case 3:
				return (
					<div className="space-y-4">
						<h3 className="text-lg font-semibold mb-4">
							Additional Information
						</h3>

						{/* Appointment Summary Card - Styled like Due Date Card */}
						<div className="flex justify-center mb-6">
							<Card className="glass-card border-0 shadow-lg bg-gradient-to-br from-primary-50 to-primary-100 border-primary-200 w-full max-w-md">
								<CardContent className="p-6">
									<div className="text-center">
										<div className="flex items-center justify-center mb-4">
											<CalendarIcon className="h-8 w-8 text-primary-600 mr-2" />
											<h4 className="text-xl font-bold text-primary-800 font-karla">
												Appointment Summary
											</h4>
										</div>

										<div className="space-y-3">
											<div>
												<p className="text-sm text-primary-700 font-karla font-medium">
													Date
												</p>
												<p className="text-lg font-bold text-primary-900 font-karla">
													{selectedDate?.toLocaleDateString("en-US", {
														weekday: "long",
														month: "long",
														day: "numeric",
														year: "numeric",
													})}
												</p>
											</div>

											<div>
												<p className="text-sm text-primary-700 font-karla font-medium">
													Time
												</p>
												<p className="text-lg font-bold text-primary-900 font-karla">
													{selectedTime}
												</p>
												<p className="text-xs text-primary-600 font-karla">
													{selectedTimezone.split("/")[1].replace("_", " ")}{" "}
													timezone
												</p>
											</div>

											<div className="pt-2 border-t border-primary-200">
												<p className="text-sm text-primary-700 font-karla">
													<span className="font-medium">Duration:</span> 30
													minutes
													<br />
													<span className="font-medium">Service:</span>{" "}
													{selectedPackage?.name || "Doula Consultation"}
												</p>
											</div>
										</div>
									</div>
								</CardContent>
							</Card>
						</div>

						<div>
							<label className="block text-sm font-medium mb-2">
								Special Notes or Requests (Optional)
							</label>
							<textarea
								value={notes}
								onChange={(e) => setNotes(e.target.value)}
								className="w-full p-3 border border-gray-200 rounded-lg resize-none"
								rows={4}
								placeholder="Any specific needs, questions, or preferences you'd like to share..."
							/>
						</div>

						<div className="bg-blue-50 p-4 rounded-lg">
							<p className="text-sm text-blue-800">
								<strong>Note:</strong> Your booking will create a Google Meet
								meeting hosted by Gaia. The doula will receive a calendar
								invitation that will block their calendar automatically.
							</p>
						</div>
					</div>
				);

			case 4:
				return (
					<div className="text-center space-y-6">
						<motion.div
							initial={{ scale: 0 }}
							animate={{ scale: 1 }}
							className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto"
						>
							<Check className="h-8 w-8 text-green-600" />
						</motion.div>

						<h3 className="text-xl font-semibold text-green-800 font-karla">
							Appointment Created Successfully!
						</h3>

						{/* Appointment Confirmation Card - Same styling as Step 3 */}
						<div className="flex justify-center">
							<Card className="glass-card border-0 shadow-lg bg-gradient-to-br from-primary-50 to-primary-100 border-primary-200 w-full max-w-md">
								<CardContent className="p-6">
									<div className="text-center">
										<div className="flex items-center justify-center mb-4">
											<CalendarIcon className="h-8 w-8 text-primary-600 mr-2" />
											<h4 className="text-xl font-bold text-primary-800 font-karla">
												Appointment Confirmed
											</h4>
										</div>

										<div className="space-y-3">
											<div>
												<p className="text-sm text-primary-700 font-karla font-medium">
													Date
												</p>
												<p className="text-lg font-bold text-primary-900 font-karla">
													{selectedDate?.toLocaleDateString("en-US", {
														weekday: "long",
														month: "long",
														day: "numeric",
														year: "numeric",
													})}
												</p>
											</div>

											<div>
												<p className="text-sm text-primary-700 font-karla font-medium">
													Time
												</p>
												<p className="text-lg font-bold text-primary-900 font-karla">
													{selectedTime}
												</p>
												<p className="text-xs text-primary-600 font-karla">
													{selectedTimezone.split("/")[1].replace("_", " ")}{" "}
													timezone
												</p>
											</div>

											<div className="pt-2 border-t border-primary-200">
												<p className="text-sm text-primary-700 font-karla">
													<span className="font-medium">Duration:</span> 30
													minutes
													<br />
													<span className="font-medium">Status:</span> Pending
													Confirmation
												</p>
											</div>

											{appointmentData?.meeting?.meetingUrl && (
												<div className="pt-2 border-t border-primary-200">
													<p className="text-sm text-primary-700 font-karla font-medium mb-2">
														Meeting Link
													</p>
													<a
														href={appointmentData.meeting.meetingUrl}
														target="_blank"
														rel="noopener noreferrer"
														className="inline-flex items-center px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium mb-2"
													>
														Join Google Meet
														<ArrowRight className="h-4 w-4 ml-2" />
													</a>
													<p className="text-xs text-primary-600 font-mono break-all">
														{appointmentData.meeting.meetingUrl}
													</p>
												</div>
											)}
										</div>
									</div>
								</CardContent>
							</Card>
						</div>
					</div>
				);

			default:
				return null;
		}
	};

	return (
		<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
			<motion.div
				initial={{ opacity: 0, scale: 0.9 }}
				animate={{ opacity: 1, scale: 1 }}
				exit={{ opacity: 0, scale: 0.9 }}
				className="glass-card rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto bg-white/95 backdrop-blur-md border border-white/20 shadow-2xl"
			>
				{/* Header with navigation and close button */}
				<div className="flex items-center justify-between p-6 border-b border-gray-200">
					<div className="flex items-center space-x-4">
						{currentStep > 1 && (
							<Button
								onClick={prevStep}
								variant="ghost"
								size="sm"
								className="text-gray-600 hover:text-gray-800"
							>
								<ArrowLeft className="h-4 w-4 mr-2" />
								Back
							</Button>
						)}
					</div>

					<div className="flex items-center space-x-4">
						{currentStep < 3 && currentStep !== 2 && (
							<Button
								onClick={nextStep}
								disabled={
									(currentStep === 1 && (!selectedDate || !selectedTimezone)) ||
									(currentStep === 2 && !selectedTime)
								}
								className="bg-gradient-to-br from-primary-50 to-primary-100 border-primary-200 text-primary-800 hover:from-primary-100 hover:to-primary-200 shadow-lg hover:shadow-xl transition-all duration-200"
							>
								Next
								<ArrowRight className="h-4 w-4 ml-2" />
							</Button>
						)}

						{currentStep === 2 && (
							<Button
								onClick={() => setCurrentStep(3)}
								className="bg-gradient-to-br from-primary-50 to-primary-100 border-primary-200 text-primary-800 hover:from-primary-100 hover:to-primary-200 shadow-lg hover:shadow-xl transition-all duration-200"
							>
								Next
								<ArrowRight className="h-4 w-4 ml-2" />
							</Button>
						)}

						{currentStep === 3 && (
							<Button
								onClick={handleBooking}
								disabled={loading}
								className="bg-gradient-to-br from-primary-600 to-primary-700 border-primary-700 text-white hover:from-primary-700 hover:to-primary-800 shadow-lg hover:shadow-xl transition-all duration-200"
							>
								{loading ? "Creating Appointment..." : "Book Intro Call"}
								<ArrowRight className="h-4 w-4 ml-2" />
							</Button>
						)}

						{/* Close Button */}
						<Button
							onClick={onCancel}
							variant="ghost"
							size="sm"
							className="bg-gradient-to-br from-primary-50 to-primary-100 border border-primary-200 text-primary-700 hover:from-primary-100 hover:to-primary-200 rounded-full p-2"
						>
							<X className="h-5 w-5" />
						</Button>
					</div>
				</div>

				{/* Content Area */}
				<div className="p-6">
					{/* Error Display */}
					{error && (
						<div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
							<div className="flex items-center">
								<AlertCircle className="h-5 w-5 text-red-600 mr-2" />
								<p className="text-sm text-red-800">{error}</p>
							</div>
						</div>
					)}

					{/* Progress Indicator */}
					<div className="flex items-center mb-6">
						{[1, 2, 3, 4].map((step) => (
							<React.Fragment key={step}>
								<div
									className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step <= currentStep
											? "bg-primary-600 text-white"
											: "bg-gray-200 text-gray-600"
										}`}
								>
									{step < currentStep ? <Check className="h-4 w-4" /> : step}
								</div>
								{step < 4 && (
									<div
										className={`flex-1 h-1 mx-2 ${step < currentStep ? "bg-primary-600" : "bg-gray-200"
											}`}
									/>
								)}
							</React.Fragment>
						))}
					</div>

					{/* Step Content */}
					<AnimatePresence mode="wait">
						<motion.div
							key={currentStep}
							initial={{ opacity: 0, x: 20 }}
							animate={{ opacity: 1, x: 0 }}
							exit={{ opacity: 0, x: -20 }}
						>
							{renderStepContent()}
						</motion.div>
					</AnimatePresence>
				</div>
			</motion.div>
		</div>
	);
};
